import { useState } from "react";
import axios from "axios";
import { Mail, Phone, MapPin, MessageCircle, Clock, CheckCircle2, AlertCircle } from "lucide-react";
import { Section, Overline, FadeIn, PageHero } from "@/components/Primitives";
import { COMPANY, IMAGES } from "@/lib/site";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INTERESTS = ["Pulses", "Oilseeds", "Cotton", "Brokerage Enquiry", "Other"];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    country: "",
    interest: "Pulses",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // 'success' | 'error' | null

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    setStatus(null);
    try {
      await axios.post(`${API}/inquiries`, form);
      setStatus("success");
      toast.success("Thank you — we'll be in touch within one business day.");
      setForm({
        name: "",
        email: "",
        company: "",
        phone: "",
        country: "",
        interest: "Pulses",
        message: "",
      });
    } catch (err) {
      setStatus("error");
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Could not send enquiry.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <Toaster position="top-center" richColors />
      <PageHero
        overline="Contact"
        title={
          <>
            Let's talk
            <br />
            <em className="not-italic text-[#2A4D3B]">commodities.</em>
          </>
        }
        subtitle="Whether you need a single container or annual contracted volumes — share your requirement, and our Dubai desk will respond promptly."
      />

      <Section testid="contact-section" className="bg-[#FDFBF7] pt-0">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <FadeIn className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="bg-white rounded-3xl border border-[#EAE7E0] p-8 sm:p-12 shadow-sm"
            >
              <Overline>Send an enquiry</Overline>
              <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl text-[#1A1A1A] leading-tight">
                Tell us what you need.
              </h2>

              <div className="mt-10 grid sm:grid-cols-2 gap-5">
                <Field label="Full name *" id="name">
                  <input
                    id="name"
                    data-testid="contact-input-name"
                    type="text"
                    required
                    value={form.name}
                    onChange={update("name")}
                    className={inputCls}
                  />
                </Field>
                <Field label="Email *" id="email">
                  <input
                    id="email"
                    data-testid="contact-input-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    className={inputCls}
                  />
                </Field>
                <Field label="Company" id="company">
                  <input
                    id="company"
                    data-testid="contact-input-company"
                    type="text"
                    value={form.company}
                    onChange={update("company")}
                    className={inputCls}
                  />
                </Field>
                <Field label="Phone / WhatsApp" id="phone">
                  <input
                    id="phone"
                    data-testid="contact-input-phone"
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    className={inputCls}
                  />
                </Field>
                <Field label="Country" id="country">
                  <input
                    id="country"
                    data-testid="contact-input-country"
                    type="text"
                    value={form.country}
                    onChange={update("country")}
                    className={inputCls}
                  />
                </Field>
                <Field label="Interest" id="interest">
                  <select
                    id="interest"
                    data-testid="contact-input-interest"
                    value={form.interest}
                    onChange={update("interest")}
                    className={`${inputCls} cursor-pointer`}
                  >
                    {INTERESTS.map((i) => (
                      <option key={i} value={i}>
                        {i}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <div className="mt-5">
                <Field label="Message *" id="message">
                  <textarea
                    id="message"
                    data-testid="contact-input-message"
                    rows={6}
                    required
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Origin / grade / volume / delivery window — anything that helps us respond accurately."
                    className={`${inputCls} resize-y`}
                  />
                </Field>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-button"
                className="mt-10 inline-flex items-center justify-center gap-2 rounded-full bg-[#2A4D3B] hover:bg-[#3A634E] disabled:opacity-60 text-[#FDFBF7] px-8 py-4 text-sm font-semibold transition-colors w-full sm:w-auto"
              >
                {submitting ? "Sending..." : "Send enquiry"}
              </button>

              {status === "success" && (
                <div
                  data-testid="contact-success"
                  className="mt-8 flex items-start gap-3 rounded-2xl bg-[#2A4D3B]/5 border border-[#2A4D3B]/20 p-5 text-[#2A4D3B]"
                >
                  <CheckCircle2 className="h-5 w-5 mt-0.5" />
                  <div className="text-sm">
                    Your enquiry is in. We'll respond within one business day at the email you provided.
                  </div>
                </div>
              )}
              {status === "error" && (
                <div
                  data-testid="contact-error"
                  className="mt-8 flex items-start gap-3 rounded-2xl bg-red-50 border border-red-200 p-5 text-red-700"
                >
                  <AlertCircle className="h-5 w-5 mt-0.5" />
                  <div className="text-sm">
                    We couldn't send your enquiry. Please email us directly at {COMPANY.email}.
                  </div>
                </div>
              )}
            </form>
          </FadeIn>

          <FadeIn delay={0.1} className="lg:col-span-5">
            <div className="rounded-3xl bg-[#1F3A2D] text-[#FDFBF7] p-10 relative overflow-hidden h-full">
              <img
                src={IMAGES.network}
                alt=""
                className="absolute inset-0 h-full w-full object-cover opacity-25"
              />
              <div className="relative">
                <Overline dark>Direct lines</Overline>
                <h3 className="mt-4 font-serif-display text-3xl">Reach the desk.</h3>

                <div className="mt-10 space-y-7 text-sm">
                  <ContactRow icon={MapPin} label="Office" value={COMPANY.address} href={COMPANY.mapsUrl} external testid="contact-address" />
                  <ContactRow
                    icon={Phone}
                    label="Phone"
                    value={COMPANY.phone}
                    href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
                    testid="contact-phone"
                  />
                  <ContactRow
                    icon={MessageCircle}
                    label="WhatsApp"
                    value={COMPANY.whatsapp}
                    href={`https://wa.me/${COMPANY.whatsapp.replace(/[^0-9]/g, "")}`}
                    testid="contact-whatsapp"
                  />
                  <ContactRow
                    icon={Mail}
                    label="Email"
                    value={COMPANY.email}
                    href={`mailto:${COMPANY.email}`}
                    testid="contact-email"
                  />
                  <ContactRow
                    icon={Clock}
                    label="Working hours"
                    value={COMPANY.hours}
                    testid="contact-hours"
                  />
                </div>

                <div className="mt-12 pt-8 border-t border-white/15 text-xs uppercase tracking-[0.2em] text-[#C8C2B5]">
                  Registered entities
                </div>
                <p className="mt-3 text-sm text-[#F6F4F0]/90 leading-relaxed">
                  {COMPANY.fze}
                  <br />
                  {COMPANY.fzco}
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* MAP SECTION */}
      <Section testid="contact-map-section" className="bg-[#F6F4F0] pt-0">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 items-start">
          <div>
            <Overline>Find us</Overline>
            <h2 className="mt-5 font-serif-display text-3xl sm:text-4xl leading-tight text-[#1A1A1A]">
              Jafza One, Jebel Ali Free Zone, Dubai.
            </h2>
            <p className="mt-5 text-sm text-[#4A4540] leading-relaxed">
              We are located inside Jafza One — one of Dubai's largest free zones, gateway to global
              maritime trade.
            </p>
            <a
              href={COMPANY.mapsUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-navigate"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#2A4D3B] hover:bg-[#3A634E] text-[#FDFBF7] px-6 py-3.5 text-sm font-semibold transition-colors"
            >
              Open in Google Maps →
            </a>
          </div>
          <div className="rounded-3xl overflow-hidden border border-[#EAE7E0] shadow-xl bg-white">
            <iframe
              title="Farmico Office Location"
              src={COMPANY.mapsEmbed}
              width="100%"
              height="420"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="contact-map-iframe"
              allowFullScreen
            />
          </div>
        </div>
      </Section>
    </main>
  );
}

const inputCls =
  "w-full rounded-xl bg-[#FDFBF7] border border-[#EAE7E0] px-4 py-3.5 text-sm text-[#1A1A1A] placeholder-[#9C958B] focus:outline-none focus:ring-2 focus:ring-[#2A4D3B]/40 focus:border-[#2A4D3B] transition";

function Field({ label, id, children }) {
  return (
    <label htmlFor={id} className="block">
      <span className="block text-xs uppercase tracking-[0.18em] text-[#4A4540] font-semibold mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}

function ContactRow({ icon: Icon, label, value, href, external, testid }) {
  const inner = (
    <>
      <Icon className="h-4 w-4 mt-1 shrink-0 text-[#D8A48F]" />
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#C8C2B5]">{label}</p>
        <p className="mt-1 text-[#FDFBF7]" data-testid={testid}>
          {value}
        </p>
      </div>
    </>
  );
  return href ? (
    <a
      href={href}
      target={external || href.startsWith("http") ? "_blank" : undefined}
      rel="noreferrer"
      className="flex gap-4 hover:opacity-80 transition-opacity"
    >
      {inner}
    </a>
  ) : (
    <div className="flex gap-4">{inner}</div>
  );
}
